/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useEffect, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';

import useAdvanceTable from 'hooks/useAdvanceTable';
import SearchBox from 'components/common/SearchBox';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import productSearchService from 'service/productSearch';
import Button from 'components/base/Button';
import { pageToOffset } from 'utils/pageToOffset';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';

export type SearchProduct = {
  name: string;
  variation: string;
  barcode: string;
  original_barcode: string;
  unit: string;
  quantity: string;
  nomenclature_id: string;
  currency_id: string;
  price_in: string;
  guid: string;
  measure_unit_kind_id: string;
  price: string;
  price_wholesale: string;
  description: string;
  sale_with_partion_code: boolean;
};

interface SearchProductToStoreProps {
  setOpenSearch: (show: boolean) => void;
  reset?: any;
  setSearchedValue?: any;
}

const SearchProductToStore = ({
  setOpenSearch,
  reset,
  setSearchedValue
}: SearchProductToStoreProps) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const inputSearchRef = useRef<HTMLInputElement | any>();

  const { data, refetch, isFetching, isLoading } = useQuery(
    ['GET_PRODUCT', searchValue, currentPage],
    () => {
      const res = productSearchService
        .getListProduct({
          offset: pageToOffset(currentPage, 10),
          limit: 10,
          name: searchValue
        })
        .then((res: any) => {
          setPageCount(Math.ceil(res?.count / 10));
          return res?.products;
        });
      return res;
    },
    { enabled: !!currentPage || !searchValue || searchValue?.length > 2 }
  );

  const loadingData = isLoading || isFetching;

  const generateData: SearchProduct[] = useMemo(() => {
    return (
      data?.map((items: any) => {
        return {
          name: items?.name,
          variation: items?.nomenclature?.is_variation ? 'ДА' : 'НЕТ',
          barcode: items?.barcode,
          original_barcode: items?.nomenclature?.barcode_original,
          unit: items?.nomenclature?.measure_unit?.name?.ru,
          quantity: items?.total_quantity ?? 0,
          nomenclature_id: items?.nomenclature_id,
          currency_id: items?.currency_id,
          price: items?.price ?? 0,
          price_in: items?.price_in ?? 0,
          price_wholesale: items?.price_whosale ?? 0,
          guid: items?.id,
          description: items?.description,
          sale_with_partion_code:
            items?.products?.[0]?.partion_codes?.length > 0 ? true : false
        };
      }) ?? []
    );
  }, [data, searchValue]);

  const searchProductTableColumns: ColumnDef<SearchProduct>[] = [
    {
      id: 'action',
      cell: rowData => {
        const createdData = {
          name: rowData?.row?.original?.name,
          barcode: rowData?.row?.original?.barcode,
          barcode_original: data?.original_barcode,
          nomenclature_id: rowData?.row?.original?.nomenclature_id,
          quantity: rowData?.row?.original?.quantity,
          currency_id: rowData?.row?.original?.currency_id,
          price: rowData?.row?.original?.price,
          price_in: rowData?.row?.original?.price_in,
          price_wholesale: rowData?.row?.original?.price_wholesale,
          description: rowData?.row?.original?.description ?? '',
          sale_with_partion_code: rowData?.row?.original?.sale_with_partion_code
        };

        return (
          <div className="d-flex gap-2">
            <Button
              variant="hover"
              className="p-2"
              onClick={() => {
                reset(createdData);
                setSearchedValue(`${rowData?.row?.original?.barcode}`);
                setOpenSearch(false);
              }}
            >
              <FeatherIcon icon="plus" className="cursor-pointer" size={20} />
            </Button>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '7%' } },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'variation',
      header: t('variation'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'original_barcode',
      header: t('barcode_original'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'unit',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'price',
      header: t('selling_price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: generateData,
    columns: searchProductTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    if (searchValue?.length > 2 || searchValue === '') {
      refetch();
    }
  }, [searchValue]);

  useEffect(() => {
    if (inputSearchRef.current) {
      inputSearchRef.current.focus();
    }
  }, [inputSearchRef]);

  return (
    <>
      <div className="mb-4">
        <SearchBox
          placeholder={t('search')}
          onChange={e => setSearchValue(e?.target?.value)}
          ref={inputSearchRef}
        />
      </div>
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          tableProps={{ className: 'phoenix-table fs-9' }}
          isLoading={loadingData}
        />
        <AdvanceTableCPagination
          count={pageCount}
          page={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </AdvanceTableProvider>
    </>
  );
};

export default SearchProductToStore;
